/*
  Cleerdent Global Animations
  Vanilla, section-safe animation layer inspired by Woodarq's theme architecture.
  Public API: window.CDCleerGlobalAnimations.refresh(), update(), destroy().
*/
(function () {
  'use strict';

  if (window.CDCleerGlobalAnimations && window.CDCleerGlobalAnimations.version) {
    return;
  }

  var VERSION = '1.0.0';
  var ROOT = document.documentElement;
  var BODY = null;
  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reducedMotion = reducedMotionQuery ? reducedMotionQuery.matches : false;
  var ticking = false;
  var resizeTimer = null;
  var motionItems = [];
  var stickyStages = [];
  var surfaceStops = [];
  var surfaceCanvas = null;
  var surfaceActive = false;
  var header = null;
  var headerLogos = [];
  var headerTextNodes = [];
  var headerIconNodes = [];
  var headerSchemes = [];
  var mutationObserver = null;

  var DEFAULTS = {
    start: 0.88,
    end: 0.52,
    once: true,
    duration: 0.8,
    y: 42,
    charY: 1.05,
    stagger: 0.028,
    wordStagger: 0.045,
    parallax: 80,
    surfaceDefault: '#f4f4f2',
    surfaceDarkest: '#151515',
    surfaceForegroundDark: '#151515',
    surfaceForegroundLight: '#f8f6ee'
  };

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function smoothstep(value) {
    var x = clamp(value, 0, 1);
    return x * x * (3 - 2 * x);
  }

  function toFloat(value, fallback) {
    var n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function toBool(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    if (value === true || value === 'true' || value === '1' || value === 'yes') return true;
    if (value === false || value === 'false' || value === '0' || value === 'no') return false;
    return fallback;
  }

  function normalizeMode(value) {
    var mode = String(value || '').trim().toLowerCase();
    if (!mode) return '';
    if (mode === 'letter' || mode === 'letterflow' || mode === 'split') return 'letters';
    if (mode === 'word' || mode === 'wordflow') return 'words';
    if (mode === 'curtain') return 'image-curtain';
    if (mode === 'up') return 'fade-up';
    return mode;
  }

  function getAttr(el, names, fallback) {
    for (var i = 0; i < names.length; i += 1) {
      if (el.hasAttribute(names[i])) return el.getAttribute(names[i]);
    }
    return fallback;
  }

  function parseColor(value, fallback) {
    var input = String(value || '').trim();
    if (!input && fallback) input = fallback;
    if (!input) return { r: 244, g: 244, b: 242 };

    if (input.charAt(0) === '#') {
      var hex = input.slice(1);
      if (hex.length === 3) {
        hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2);
      }
      if (hex.length === 6) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16)
        };
      }
    }

    var rgb = input.match(/rgba?\(([^)]+)\)/i);
    if (rgb) {
      var parts = rgb[1].split(',').map(function (part) { return parseFloat(part.trim()); });
      if (parts.length >= 3) {
        return { r: parts[0], g: parts[1], b: parts[2] };
      }
    }

    return parseColor(fallback || DEFAULTS.surfaceDefault);
  }

  function colorToCss(color) {
    return 'rgb(' + Math.round(color.r) + ', ' + Math.round(color.g) + ', ' + Math.round(color.b) + ')';
  }

  function colorToRgbString(color) {
    return Math.round(color.r) + ', ' + Math.round(color.g) + ', ' + Math.round(color.b);
  }

  function mixColor(a, b, t) {
    return {
      r: lerp(a.r, b.r, t),
      g: lerp(a.g, b.g, t),
      b: lerp(a.b, b.b, t)
    };
  }

  function luminance(color) {
    function channel(value) {
      var v = value / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
  }

  function foregroundFor(bg) {
    var blend = clamp((0.62 - luminance(bg)) / 0.44, 0, 1);
    return mixColor(parseColor(DEFAULTS.surfaceForegroundDark), parseColor(DEFAULTS.surfaceForegroundLight), blend);
  }

  function foregroundBlend(bg) {
    return clamp((0.62 - luminance(bg)) / 0.44, 0, 1);
  }

  function findAll(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function canSplitNode(node) {
    if (!node || node.nodeType !== 3) return false;
    return !!(node.nodeValue && node.nodeValue.trim());
  }

  function createSpace(text) {
    var span = document.createElement('span');
    span.className = 'cd-motion-space';
    span.textContent = text;
    return span;
  }

  function createChar(char, index) {
    var span = document.createElement('span');
    span.className = 'cd-motion-char';
    span.textContent = char;
    span.setAttribute('data-cd-motion-char-index', String(index));
    span.style.setProperty('--cd-motion-char-opacity', '0');
    span.style.setProperty('--cd-motion-char-y', DEFAULTS.charY + 'em');
    return span;
  }

  function splitTextNodeIntoLetters(text, counter) {
    var fragment = document.createDocumentFragment();
    var parts = text.split(/(\s+)/);

    parts.forEach(function (part) {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.appendChild(createSpace(part));
        return;
      }

      var word = document.createElement('span');
      word.className = 'cd-motion-word';
      for (var i = 0; i < part.length; i += 1) {
        word.appendChild(createChar(part.charAt(i), counter.value));
        counter.value += 1;
      }
      fragment.appendChild(word);
    });

    return fragment;
  }

  function createWord(part, index) {
    var outer = document.createElement('span');
    var inner = document.createElement('span');
    outer.className = 'cd-motion-word';
    inner.className = 'cd-motion-word-inner';
    inner.textContent = part;
    inner.setAttribute('data-cd-motion-word-index', String(index));
    inner.style.setProperty('--cd-motion-word-opacity', '0');
    inner.style.setProperty('--cd-motion-word-y', DEFAULTS.charY + 'em');
    outer.appendChild(inner);
    return outer;
  }

  function splitTextNodeIntoWords(text, counter) {
    var fragment = document.createDocumentFragment();
    var parts = text.split(/(\s+)/);

    parts.forEach(function (part) {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.appendChild(createSpace(part));
        return;
      }
      fragment.appendChild(createWord(part, counter.value));
      counter.value += 1;
    });

    return fragment;
  }

  function splitTree(source, target, mode, counter) {
    Array.prototype.slice.call(source.childNodes).forEach(function (child) {
      if (canSplitNode(child)) {
        target.appendChild(mode === 'words' ? splitTextNodeIntoWords(child.nodeValue, counter) : splitTextNodeIntoLetters(child.nodeValue, counter));
        return;
      }

      if (child.nodeType === 3) {
        target.appendChild(document.createTextNode(child.nodeValue || ''));
        return;
      }

      if (child.nodeType !== 1) return;

      var tagName = child.tagName ? child.tagName.toLowerCase() : '';
      if (tagName === 'script' || tagName === 'style' || tagName === 'svg' || tagName === 'noscript') {
        target.appendChild(child.cloneNode(true));
        return;
      }

      var clone = child.cloneNode(false);
      splitTree(child, clone, mode, counter);
      target.appendChild(clone);
    });
  }

  function splitTextElement(el, mode) {
    if (!el || el.getAttribute('data-cd-motion-split-ready') === 'true') return;

    var originalText = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!originalText) return;

    var counter = { value: 0 };
    var fragment = document.createDocumentFragment();
    splitTree(el, fragment, mode, counter);

    el.innerHTML = '';
    el.appendChild(fragment);
    el.classList.add('cd-motion-split-ready');
    el.setAttribute('data-cd-motion-split-ready', 'true');
    el.setAttribute('data-cd-motion-char-total', String(counter.value));

    if (!el.hasAttribute('aria-label')) {
      el.setAttribute('aria-label', originalText);
    }
  }

  function getMotionMode(el) {
    return normalizeMode(
      getAttr(el, ['data-cd-animate', 'data-cd-motion', 'data-cd-split'], '')
    );
  }

  function buildMotionItem(el) {
    var mode = getMotionMode(el);
    if (!mode) return null;

    var splitMode = mode === 'letters' || mode === 'words' ? mode : normalizeMode(el.getAttribute('data-cd-split'));
    if (splitMode === 'letters' || splitMode === 'words') {
      splitTextElement(el, splitMode);
    }

    var childrenSelector = el.getAttribute('data-cd-stagger-children');
    var childNodes = [];
    if (childrenSelector) {
      childNodes = findAll(childrenSelector, el);
      childNodes.forEach(function (child) { child.setAttribute('data-cd-motion-child', 'true'); });
    }

    return {
      el: el,
      mode: mode,
      splitMode: splitMode,
      chars: findAll('.cd-motion-char', el),
      words: findAll('.cd-motion-word-inner', el),
      children: childNodes,
      start: toFloat(el.getAttribute('data-cd-motion-start'), DEFAULTS.start),
      end: toFloat(el.getAttribute('data-cd-motion-end'), DEFAULTS.end),
      once: toBool(el.getAttribute('data-cd-motion-once'), DEFAULTS.once),
      stagger: toFloat(el.getAttribute('data-cd-motion-stagger'), splitMode === 'words' ? DEFAULTS.wordStagger : DEFAULTS.stagger),
      y: toFloat(el.getAttribute('data-cd-motion-y'), DEFAULTS.y),
      charY: toFloat(el.getAttribute('data-cd-motion-char-y'), DEFAULTS.charY),
      parallax: toFloat(el.getAttribute('data-cd-motion-parallax'), DEFAULTS.parallax),
      progress: 0,
      locked: false
    };
  }

  function collectMotionItems() {
    var nodes = findAll('[data-cd-animate], [data-cd-motion], [data-cd-split]');
    motionItems = [];

    nodes.forEach(function (node) {
      if (node.closest('[data-cd-motion-ignore]')) return;
      var item = buildMotionItem(node);
      if (item) motionItems.push(item);
    });
  }

  function itemProgress(item) {
    if (reducedMotion) return 1;
    if (item.locked) return 1;

    var rect = item.el.getBoundingClientRect();
    var vh = Math.max(window.innerHeight || document.documentElement.clientHeight || 1, 1);
    var startY = vh * item.start;
    var endY = vh * item.end;
    var raw = (startY - rect.top) / (startY - endY);
    return smoothstep(raw);
  }

  function setNodeProgress(node, progress, y, mode) {
    var p = clamp(progress, 0, 1);
    var opacity = p;
    var translateY = (1 - p) * y;
    var scale = 0.965 + 0.035 * p;
    var clipRight = (1 - p) * 100;

    node.style.setProperty('--cd-motion-progress', p.toFixed(4));
    node.style.setProperty('--cd-motion-opacity', opacity.toFixed(4));
    node.style.setProperty('--cd-motion-y', translateY.toFixed(2) + 'px');
    node.style.setProperty('--cd-motion-scale', scale.toFixed(4));
    node.style.setProperty('--cd-motion-scale-x', p.toFixed(4));
    node.style.setProperty('--cd-motion-clip-right', clipRight.toFixed(2) + '%');

    if (mode === 'image-curtain' || mode === 'clip') {
      var img = node.querySelector('img, picture, video');
      if (img) {
        img.style.setProperty('--cd-motion-scale', (1.04 - 0.04 * p).toFixed(4));
      }
    }
  }

  function applySplitProgress(item, progress) {
    var nodes = item.splitMode === 'words' ? item.words : item.chars;
    if (!nodes || !nodes.length) return;

    var total = nodes.length;
    var stagger = item.stagger;
    var span = Math.max(1, 1 + stagger * Math.max(total - 1, 0));

    nodes.forEach(function (node, index) {
      var local = smoothstep((progress * span - index * stagger));
      if (item.splitMode === 'words') {
        node.style.setProperty('--cd-motion-word-opacity', local.toFixed(4));
        node.style.setProperty('--cd-motion-word-y', ((1 - local) * item.charY).toFixed(3) + 'em');
      } else {
        node.style.setProperty('--cd-motion-char-opacity', local.toFixed(4));
        node.style.setProperty('--cd-motion-char-y', ((1 - local) * item.charY).toFixed(3) + 'em');
      }
    });
  }

  function applyChildrenProgress(item, progress) {
    if (!item.children || !item.children.length) return;
    var total = item.children.length;
    var stagger = toFloat(item.el.getAttribute('data-cd-motion-child-stagger'), 0.075);
    var span = Math.max(1, 1 + stagger * Math.max(total - 1, 0));

    item.children.forEach(function (node, index) {
      var local = smoothstep((progress * span - index * stagger));
      setNodeProgress(node, local, item.y, 'fade-up');
    });
  }

  function applyMotionItem(item) {
    var progress = itemProgress(item);
    item.progress = progress;

    if (item.mode === 'letters' || item.mode === 'words') {
      applySplitProgress(item, progress);
    } else if (item.mode === 'parallax') {
      var rect = item.el.getBoundingClientRect();
      var vh = Math.max(window.innerHeight || 1, 1);
      var centerProgress = ((rect.top + rect.height * 0.5) - vh * 0.5) / vh;
      item.el.style.setProperty('--cd-motion-parallax-y', (-centerProgress * item.parallax).toFixed(2) + 'px');
    } else {
      setNodeProgress(item.el, progress, item.y, item.mode);
    }

    applyChildrenProgress(item, progress);

    if (progress >= 0.995 && item.once) {
      item.locked = true;
      item.el.setAttribute('data-cd-motion-state', 'done');
      if (item.mode !== 'parallax') {
        if (item.mode === 'letters' || item.mode === 'words') {
          applySplitProgress(item, 1);
        } else {
          setNodeProgress(item.el, 1, item.y, item.mode);
        }
        applyChildrenProgress(item, 1);
      }
    }
  }

  function collectStickyStages() {
    stickyStages = findAll('[data-cd-sticky-stage]').map(function (el) {
      return {
        el: el,
        top: toFloat(el.getAttribute('data-cd-sticky-top'), 0)
      };
    });
  }

  function applyStickyStage(stage) {
    var rect = stage.el.getBoundingClientRect();
    var vh = Math.max(window.innerHeight || 1, 1);
    var scrollable = Math.max(rect.height - vh, 1);
    var raw = clamp((stage.top - rect.top) / scrollable, 0, 1);
    var enter = clamp((vh - rect.top) / vh, 0, 1);
    var leave = clamp((vh - rect.bottom) / vh, 0, 1);

    stage.el.style.setProperty('--cd-sticky-progress', raw.toFixed(4));
    stage.el.style.setProperty('--cd-sticky-enter', enter.toFixed(4));
    stage.el.style.setProperty('--cd-sticky-leave', leave.toFixed(4));
  }

  function surfaceSelectors() {
    return '[data-cd-global-surface-start], [data-cd-global-surface], [data-cd-surface-color]';
  }

  function ensureSurfaceCanvas() {
    if (surfaceCanvas && document.body && document.body.contains(surfaceCanvas)) return surfaceCanvas;
    surfaceCanvas = document.getElementById('cd-global-surface-canvas');
    if (!surfaceCanvas && document.body) {
      surfaceCanvas = document.createElement('div');
      surfaceCanvas.id = 'cd-global-surface-canvas';
      surfaceCanvas.setAttribute('aria-hidden', 'true');
      document.body.insertBefore(surfaceCanvas, document.body.firstChild);
    }
    return surfaceCanvas;
  }

  function collectSurfaceStops() {
    surfaceStops = [];

    var nodes = findAll(surfaceSelectors());
    nodes.forEach(function (el) {
      var startColor = getAttr(el, ['data-cd-global-surface-start', 'data-cd-global-surface', 'data-cd-surface-color'], null);
      if (!startColor) return;
      var endColor = getAttr(el, ['data-cd-global-surface-end'], startColor);
      var floor = getAttr(el, ['data-cd-global-surface-floor'], DEFAULTS.surfaceDarkest);

      surfaceStops.push({
        el: el,
        startColor: parseColor(startColor, DEFAULTS.surfaceDefault),
        endColor: parseColor(endColor, startColor),
        floor: parseColor(floor, DEFAULTS.surfaceDarkest),
        startOffset: toFloat(el.getAttribute('data-cd-global-surface-start-offset'), 0.82),
        endOffset: toFloat(el.getAttribute('data-cd-global-surface-end-offset'), 0.18),
        top: 0,
        bottom: 0
      });
    });

    surfaceActive = surfaceStops.length > 0;
    if (surfaceActive) {
      ensureSurfaceCanvas();
      ROOT.classList.add('cd-global-surface-ready');
    } else {
      ROOT.classList.remove('cd-global-surface-ready');
    }
  }

  function measureSurfaceStops() {
    var pageY = window.scrollY || window.pageYOffset || 0;
    surfaceStops.forEach(function (stop) {
      var rect = stop.el.getBoundingClientRect();
      stop.top = rect.top + pageY;
      stop.bottom = stop.top + rect.height;
    });
  }

  function clampSurfaceFloor(color, floor) {
    return {
      r: Math.max(color.r, floor.r),
      g: Math.max(color.g, floor.g),
      b: Math.max(color.b, floor.b)
    };
  }

  function findSurfaceColor() {
    if (!surfaceStops.length) return null;

    var pageY = window.scrollY || window.pageYOffset || 0;
    var vh = Math.max(window.innerHeight || 1, 1);
    var anchor = pageY + vh * 0.52;
    var chosen = surfaceStops[0].startColor;
    var chosenFloor = surfaceStops[0].floor;

    for (var i = 0; i < surfaceStops.length; i += 1) {
      var stop = surfaceStops[i];
      var start = stop.top - vh * stop.startOffset;
      var end = stop.bottom - vh * stop.endOffset;
      var local = smoothstep((anchor - start) / Math.max(end - start, 1));
      if (anchor >= start) {
        chosen = mixColor(stop.startColor, stop.endColor, local);
        chosenFloor = stop.floor;
      }
    }

    return clampSurfaceFloor(chosen, chosenFloor);
  }

  function collectHeaderTargets() {
    header = document.querySelector('[data-cd-hero-header]') || document.querySelector('.cd-hero-header__wrapper') || document.querySelector('#shopify-section-header-inline');
    headerLogos = findAll('[data-cd-hero-header] .cd-hero-header__logo img, [data-cd-hero-header] .cd-hero-header__mobile-logo img, .cd-hero-header__logo img, .cd-hero-header__mobile-logo img');
    headerTextNodes = findAll('[data-cd-hero-header] a, [data-cd-hero-header] button, [data-cd-hero-header] .cd-hero-header__nav-link, [data-cd-hero-header] .cd-hero-header__cart-link, [data-cd-hero-header] .cd-hero-header__menu-trigger');
    headerIconNodes = findAll('[data-cd-hero-header] svg, [data-cd-hero-header] svg *, .cd-hero-header__wrapper svg, .cd-hero-header__wrapper svg *');
    headerSchemes = findAll('[data-cd-header-scheme]');
  }

  function applyHeaderColor(fg, fgRgb, invertAmount) {
    if (!header) return;

    header.style.setProperty('--h-text-color', fg);
    header.style.setProperty('--h-text-color-rgb', fgRgb);
    header.style.setProperty('--h-text-color-hover', 'rgba(' + fgRgb + ', 0.78)');
    header.style.color = fg;

    headerTextNodes.forEach(function (node) {
      node.style.color = fg;
    });

    headerIconNodes.forEach(function (node) {
      node.style.color = fg;
      if (node.tagName && node.tagName.toLowerCase() !== 'svg') {
        node.style.stroke = 'currentColor';
        if (node.getAttribute && node.getAttribute('fill') && node.getAttribute('fill') !== 'none') {
          node.style.fill = 'currentColor';
        }
      }
    });

    headerLogos.forEach(function (logo) {
      logo.style.filter = 'invert(' + invertAmount.toFixed(3) + ') brightness(1.02)';
    });
  }

  function applySurface() {
    if (!surfaceActive || !surfaceCanvas) return;
    var color = findSurfaceColor();
    if (!color) return;

    var fg = foregroundFor(color);
    var fgCss = colorToCss(fg);
    var fgRgb = colorToRgbString(fg);
    var fgMuted = colorToCss(mixColor(fg, color, 0.36));
    var colorCss = colorToCss(color);
    var invert = foregroundBlend(color);

    surfaceCanvas.style.backgroundColor = colorCss;
    ROOT.style.setProperty('--cd-global-surface-color', colorCss);
    ROOT.style.setProperty('--cd-global-surface-fg', fgCss);
    ROOT.style.setProperty('--cd-global-surface-fg-rgb', fgRgb);
    ROOT.style.setProperty('--cd-global-surface-fg-hover', 'rgba(' + fgRgb + ', 0.78)');
    ROOT.style.setProperty('--cd-global-surface-fg-muted', fgMuted);
    ROOT.style.setProperty('--cd-global-surface-line', 'rgba(' + fgRgb + ', 0.16)');
    ROOT.style.setProperty('--cd-global-surface-line-strong', 'rgba(' + fgRgb + ', 0.24)');
    ROOT.style.setProperty('--cd-global-surface-logo-invert', invert.toFixed(3));

    applyHeaderColor(fgCss, fgRgb, invert);
  }

  function activeHeaderScheme() {
    if (!headerSchemes.length) return null;

    var headerRect = header ? header.getBoundingClientRect() : { top: 0, bottom: 96 };
    var y = Math.max(headerRect.bottom, 1);
    var active = null;

    headerSchemes.forEach(function (section) {
      var rect = section.getBoundingClientRect();
      if (rect.top <= y && rect.bottom >= 0) active = section;
    });

    return active;
  }

  function isExternalSurfaceControllerActive() {
    var body = BODY || document.body;

    if (body && body.classList.contains('template-index')) {
      if (ROOT.classList.contains('cd-home-surface-ready') && document.getElementById('cd-home-surface-canvas')) {
        return true;
      }
    }

    if (body && body.classList.contains('template-product')) {
      if (ROOT.classList.contains('cd-pdp-surface-ready') && document.querySelector('[data-cd-pdp-surface-start]')) {
        return true;
      }
    }

    return false;
  }

  function applyHeaderScheme() {
    /*
      The home/PDP surface blenders continuously calculate the header foreground
      from the actual blended surface color. When either one is active, this
      generic header-scheme fallback must stay out of the way, otherwise the
      two controllers can write opposite colors on adjacent animation frames.
    */
    if (surfaceActive || isExternalSurfaceControllerActive()) return;
    var section = activeHeaderScheme();
    if (!section) return;

    var scheme = String(section.getAttribute('data-cd-header-scheme') || '').toLowerCase();
    var fg = scheme === 'light' ? DEFAULTS.surfaceForegroundLight : DEFAULTS.surfaceForegroundDark;
    var rgb = colorToRgbString(parseColor(fg));
    var invert = scheme === 'light' ? 1 : 0;
    applyHeaderColor(fg, rgb, invert);
  }

  function update() {
    ticking = false;

    if (reducedMotion) {
      motionItems.forEach(function (item) {
        if (item.mode === 'letters' || item.mode === 'words') applySplitProgress(item, 1);
        else setNodeProgress(item.el, 1, item.y, item.mode);
        applyChildrenProgress(item, 1);
      });
      return;
    }

    motionItems.forEach(applyMotionItem);
    stickyStages.forEach(applyStickyStage);
    applySurface();
    applyHeaderScheme();
  }

  function queueUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function refresh() {
    BODY = document.body;
    if (!BODY) return;

    ROOT.classList.remove('cd-global-motion-pending');
    ROOT.classList.add('cd-global-motion-ready');

    collectHeaderTargets();
    collectMotionItems();
    collectStickyStages();
    collectSurfaceStops();
    measureSurfaceStops();
    queueUpdate();
  }

  function scheduleRefresh() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      refresh();
    }, 80);
  }

  function onFontsReady() {
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () {
        refresh();
      }).catch(function () {
        refresh();
      });
    } else {
      refresh();
    }
  }

  function bindMutationObserver() {
    if (!window.MutationObserver || mutationObserver) return;
    mutationObserver = new MutationObserver(function (mutations) {
      var shouldRefresh = mutations.some(function (mutation) {
        if (!mutation.addedNodes || !mutation.addedNodes.length) return false;
        return Array.prototype.slice.call(mutation.addedNodes).some(function (node) {
          if (!node || node.nodeType !== 1) return false;
          if (node.matches && node.matches('[data-cd-animate], [data-cd-motion], [data-cd-split], [data-cd-global-surface-start], [data-cd-header-scheme], [data-cd-sticky-stage]')) return true;
          return node.querySelector && node.querySelector('[data-cd-animate], [data-cd-motion], [data-cd-split], [data-cd-global-surface-start], [data-cd-header-scheme], [data-cd-sticky-stage]');
        });
      });
      if (shouldRefresh) scheduleRefresh();
    });
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  function destroy() {
    window.removeEventListener('scroll', queueUpdate);
    window.removeEventListener('resize', scheduleRefresh);
    window.removeEventListener('orientationchange', scheduleRefresh);
    document.removeEventListener('shopify:section:load', scheduleRefresh);
    document.removeEventListener('shopify:section:reorder', scheduleRefresh);
    document.removeEventListener('shopify:section:unload', scheduleRefresh);
    if (mutationObserver) mutationObserver.disconnect();
    mutationObserver = null;
    motionItems = [];
    stickyStages = [];
    surfaceStops = [];
    ticking = false;
  }

  function init() {
    bindMutationObserver();
    onFontsReady();
    window.addEventListener('load', scheduleRefresh);
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', scheduleRefresh);
    window.addEventListener('orientationchange', scheduleRefresh);
    document.addEventListener('shopify:section:load', scheduleRefresh);
    document.addEventListener('shopify:section:reorder', scheduleRefresh);
    document.addEventListener('shopify:section:unload', scheduleRefresh);

    if (reducedMotionQuery && reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', function (event) {
        reducedMotion = event.matches;
        scheduleRefresh();
      });
    }
  }

  window.CDCleerGlobalAnimations = {
    version: VERSION,
    refresh: refresh,
    update: queueUpdate,
    destroy: destroy
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
